import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const LINE_FALLBACK_PX = 24;
const INITIAL_LINES = 5;
const AUTO_SCROLL_EDGE_PX = 44;
const AUTO_SCROLL_SPEED_PX = 10;
const AUTO_SCROLL_ARM_PX = 8; // pointer must move this far toward an edge before that edge can auto-scroll, so a drag starting at the edge (e.g. right after a previous auto-scroll) doesn't immediately fight a reversal
const RECT_INSET_PX = 8; // selection rect's horizontal inset within the transcript column
const BOTTOM_STOP_MARGIN_PX = 24; // the handle overhangs 9px below the selection border (see .clip-line-selector-handle-bottom); keep enough clearance for the whole handle, not just the border line

export const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const ClipLineSelector = ({ active, preparing, bodies, initialItem, onCancel, onConfirm }) => {
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const scrollParentRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [sel, setSel] = useState(null); // { top, bottom } px within the transcript list
  const selRef = useRef(sel);
  selRef.current = sel;
  // Viewport box of the selection. The dim + the interactive rect are portaled to <body> and
  // positioned from this, so the overhanging handles paint above the dim instead of under it.
  const [dimClip, setDimClip] = useState(null);

  const rows = () => Array.from(rootRef.current?.parentElement?.querySelectorAll('[id^="event-"]') ?? []);
  const originTop = () => rootRef.current?.getBoundingClientRect().top ?? 0;
  const contentHeight = () => rootRef.current?.offsetHeight ?? 0;

  const topBoundaryVp = () => {
    const scrollerTop =
      (scrollParentRef.current ?? rootRef.current?.closest('.custom-scrollbars__content'))?.getBoundingClientRect()
        .top ?? 0;
    const title = document.querySelector('.content-item-scrolled-title.item-scrolled-title-visible');
    const searchBar = document.querySelector('.transcript-search-bar');
    return Math.max(
      scrollerTop,
      title?.getBoundingClientRect().bottom ?? scrollerTop,
      searchBar?.getBoundingClientRect().bottom ?? scrollerTop
    );
  };

  const bottomBoundaryVp = () =>
    (scrollParentRef.current ?? rootRef.current?.closest('.custom-scrollbars__content'))?.getBoundingClientRect()
      .bottom ?? window.innerHeight;

  const lineHeight = () => {
    const p = rootRef.current?.parentElement?.querySelector('.parliament-live-event p.paragraph-p1');
    return (p && parseFloat(getComputedStyle(p).lineHeight)) || LINE_FALLBACK_PX;
  };

  const rowRanges = () => {
    const top0 = originTop();
    return rows().map((row) => {
      const box = row.getBoundingClientRect();
      return { top: box.top - top0, bottom: box.bottom - top0 };
    });
  };

  // Snap y to the nearest line boundary of whichever row it lands in — keeps the grid aligned to
  // real text lines instead of accumulating offset over the paragraph gaps.
  const snapToLine = (y) => {
    const grid = lineHeight();
    const ranges = rowRanges();
    const row =
      ranges.find((r) => y >= r.top - grid && y <= r.bottom + grid) ??
      (y <= (ranges[0]?.top ?? 0) ? ranges[0] : ranges[ranges.length - 1]);
    if (!row) return Math.round(y / grid) * grid;
    const snapped = row.top + Math.round((y - row.top) / grid) * grid;
    return Math.max(row.top, Math.min(snapped, row.bottom));
  };

  // Each row's share of the selection, by pixel overlap — a row only half inside the drag
  // handles contributes half its duration instead of the whole paragraph's.
  const coveredItems = (range) => {
    if (!range) return [];
    return rowRanges().reduce((acc, r, i) => {
      const overlap = Math.min(r.bottom, range.bottom) - Math.max(r.top, range.top);
      if (overlap > 0) acc.push({ body: bodies[i], fraction: overlap / (r.bottom - r.top || 1) });
      return acc;
    }, []);
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
    autoScrollRef.current = null;
  };

  // The in-flow root scrolls with the transcript; project the current selection onto the viewport
  // through it, so the portaled dim + rect track it.
  const syncDimClip = (s = selRef.current) => {
    const root = rootRef.current;
    if (!root || !s) return setDimClip(null);
    const o = root.getBoundingClientRect();
    setDimClip({
      top: Math.round(o.top + s.top),
      left: Math.round(o.left + RECT_INSET_PX),
      right: Math.round(o.right - RECT_INSET_PX),
      bottom: Math.round(o.top + s.bottom),
    });
  };

  useLayoutEffect(() => {
    if (!active) {
      setSel(null);
      setDimClip(null);
      return;
    }
    if (!rows().length) return;

    const targetIndex = initialItem ? bodies.findIndex((b) => b?.objectID === initialItem?.objectID) : -1;
    const targetRange = targetIndex >= 0 ? rowRanges()[targetIndex] : null;
    if (targetRange) {
      setSel({ top: targetRange.top, bottom: targetRange.bottom });
      return;
    }

    const height = lineHeight() * INITIAL_LINES;
    const centre = window.innerHeight / 2 - originTop() - height / 2;
    const top = Math.max(0, Math.min(snapToLine(centre), contentHeight() - height));
    setSel({ top, bottom: top + height });
  }, [active]);

  useLayoutEffect(() => {
    if (active) syncDimClip();
  }, [sel, active]);

  useEffect(() => {
    if (!active) return;
    let raf = null;
    const onViewChange = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        syncDimClip();
      });
    };
    window.addEventListener('scroll', onViewChange, true);
    window.addEventListener('resize', onViewChange);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      stopAutoScroll();
      document.body.style.cursor = '';
      window.removeEventListener('scroll', onViewChange, true);
      window.removeEventListener('resize', onViewChange);
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, onCancel]);

  const beginDrag = (mode) => (e) => {
    if (preparing) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ns-resize';
    scrollParentRef.current = rootRef.current?.closest('.custom-scrollbars__content') ?? null;

    dragRef.current = {
      mode,
      startY: e.clientY,
      lastY: e.clientY,
      armedTop: false,
      armedBottom: false,
      top: sel.top,
      bottom: sel.bottom,
      originTop0: originTop(),
    };
  };

  const applyDrag = (clientY) => {
    const drag = dragRef.current;
    if (!drag) return;
    const grid = lineHeight();
    const delta = clientY - drag.startY + (drag.originTop0 - originTop());
    const minTop = Math.max(0, topBoundaryVp() - originTop());

    const maxY = Math.min(
      contentHeight() - BOTTOM_STOP_MARGIN_PX,
      bottomBoundaryVp() - originTop() - BOTTOM_STOP_MARGIN_PX
    );
    let next;
    if (drag.mode === 'top') {
      const bottom = Math.min(drag.bottom, maxY);
      const top = Math.min(bottom - grid, Math.max(minTop, snapToLine(drag.top + delta)));
      next = { top, bottom };
    } else if (drag.mode === 'bottom') {
      const top = Math.max(drag.top, minTop);
      const bottom = Math.max(top + grid, Math.min(maxY, snapToLine(drag.bottom + delta)));
      next = { top, bottom };
    } else {
      const height = drag.bottom - drag.top;
      const top = Math.min(maxY - height, Math.max(minTop, snapToLine(drag.top + delta)));
      next = { top, bottom: top + height };
    }
    setSel(next);
    syncDimClip(next);
  };

  const autoScrollStep = () => {
    const drag = dragRef.current;
    const scroller = scrollParentRef.current;
    if (!drag || !scroller) return stopAutoScroll();
    const box = scroller.getBoundingClientRect();
    const topEdge = topBoundaryVp();
    const selTopVp = originTop() + (selRef.current?.top ?? 0);
    const selTopInZone = drag.mode !== 'bottom' && selTopVp < topEdge + AUTO_SCROLL_EDGE_PX;
    const nearTop = drag.armedTop && (drag.lastY < topEdge + AUTO_SCROLL_EDGE_PX || selTopInZone);
    const dir = nearTop ? -1 : drag.armedBottom && drag.lastY > box.bottom - AUTO_SCROLL_EDGE_PX ? 1 : 0;
    if (dir === 0) return stopAutoScroll();
    const before = scroller.scrollTop;
    scroller.scrollTop = before + dir * AUTO_SCROLL_SPEED_PX;
    if (scroller.scrollTop !== before) {
      applyDrag(drag.lastY);
    }
    autoScrollRef.current = requestAnimationFrame(autoScrollStep);
  };

  const endDrag = () => {
    document.body.style.cursor = '';
    dragRef.current = null;
    stopAutoScroll();
  };

  const onMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (e.buttons === 0) return endDrag();
    drag.lastY = e.clientY;
    if (e.clientY < drag.startY - AUTO_SCROLL_ARM_PX) drag.armedTop = true;
    if (e.clientY > drag.startY + AUTO_SCROLL_ARM_PX) drag.armedBottom = true;
    applyDrag(e.clientY);
    if (!autoScrollRef.current) autoScrollRef.current = requestAnimationFrame(autoScrollStep);
  };

  if (!active || !bodies.length) return null;

  const covered = coveredItems(sel);
  const selected = covered.map((c) => c.body);
  const durationSeconds = covered.reduce((sum, { body, fraction }) => sum + (body.duration ?? 0) * fraction, 0);

  const dimBox = dimClip && {
    top: dimClip.top,
    left: dimClip.left,
    width: dimClip.right - dimClip.left,
    height: dimClip.bottom - dimClip.top,
  };

  return (
    <>
      <div ref={rootRef} className='clip-line-selector' />
      {createPortal(
        <>
          <div className='clip-line-selector-catcher' onPointerDown={onCancel} />
          {dimBox && (
            <>
              <div className='clip-line-selector-dim' style={dimBox} />
              <div
                className='clip-line-selector-rect'
                style={dimBox}
                onPointerMove={onMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <button
                  type='button'
                  className='clip-line-selector-handle clip-line-selector-handle-top'
                  aria-label='Adjust clip start'
                  onPointerDown={beginDrag('top')}
                />
                <div className='clip-line-selector-body' onPointerDown={beginDrag('move')} />
                <button
                  type='button'
                  className='clip-line-selector-handle clip-line-selector-handle-bottom'
                  aria-label='Adjust clip end'
                  onPointerDown={beginDrag('bottom')}
                />
              </div>
            </>
          )}
          <div className='clip-line-selector-toolbar position-fixed top-0 bg-main-blue position-fixed w-100 py-3 adjustment-of-position'>
            <div className='mx-auto main-content-wrapper row mx-auto align-items-center justify-content-between '>
              <div className='position-relative show-lg side-left-element  pl-3 pl-lg-5'>
                <img
                  alt='Logo'
                  className='policy-logo '
                  src={`${import.meta.env.VITE_APP_CDNURL}/images/logo_white.svg`}
                />
              </div>
              <div className='main-content flex-grow-1 d-flex justify-content-between  pr-xl-5 px-md-4 px-3'>
                <div className='flex-centered specific-y-padding-4 specific-x-padding-5 specific-left-padding-lg-16  bg-main-white rounded-pill flex-centered '>
                  <p className='mb-0 clip-tool-bar-duration d-none d-lg-block'>
                    <span className='font-weight-bold'>Maximum duration:</span> 3 minutes{' '}
                    <span className='dot mx-2 bg-light-blue d-inline-block-centered centered-2' />
                    <span className='font-weight-bold'>Clip duration: </span>
                    <span className='text-light-blue'>{formatDuration(durationSeconds)}</span>
                  </p>

                  <button
                    type='button'
                    className='general-button action-button rounded-pill px-4 specific-y-padding-2 ml-lg-2'
                    disabled={preparing || !selected.length}
                    onClick={() => onConfirm(selected)}
                    style={{ minWidth: '126px' }}
                  >
                    {preparing ? (
                      <i className='fas fa-spinner fa-spin d-inline-block-centered centered-5' />
                    ) : (
                      'Create clip'
                    )}
                  </button>
                </div>

                <div className='flex-centered'>
                  <button
                    type='button'
                    className='h-100 d-inline-block general-button bg-main-white rounded-pill px-3 px-xl-4 hovered-button'
                    onClick={onCancel}
                  >
                    Exit <span className='d-none d-xl-inline-block'>clip mode</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default ClipLineSelector;
